import React, {useState} from 'react';
import {Box, Button, CircularProgress, TextField, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Alert, AlertTitle} from "@/components/ui/alert";
import {Card, CardContent, CardHeader} from "@/components/ui/card";
import {Check} from "lucide-react";
import apiService from "@/service/api-service";
import {store} from "@/service/store";
import {zipCodeRegex} from "@/utils/patterns.ts";

interface CustomerConnectStepProps {
  customerNumber: string;
  setCustomerConnected: (connected: boolean) => void;
}

interface CustomerResponse {
  uuid: string;
  businessName: string;
  legalForm: string;
  customerNumber: number;
  streetOne: string;
  streetTwo?: string;
  zipCode: string;
  city: string;
  phoneNumber: string;
  mobileNumber: string;
  emailAddress: string;
  contactPerson: string;
  owner: string;
  ownerBirthdate: string;
  createdAt: string;
}

const CustomerConnectStep: React.FC<CustomerConnectStepProps> = ({
  customerNumber,
  setCustomerConnected
}) => {
  const { t } = useTranslation();
  const [zipCode, setZipCode] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zipCodeError, setZipCodeError] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<CustomerResponse | null>(null);

  const validateZipCode = (value: string) => {
    if (!zipCodeRegex.test(value)) {
      setZipCodeError(t('assistant.customerConnectStep.invalidZipCode'));
      return false;
    }
    setZipCodeError(null);
    return true;
  };

  const handleConnect = async () => {
    if (!zipCode) {
      setError(t('assistant.customerConnectStep.allFieldsRequired'));
      return;
    }

    if (!validateZipCode(zipCode)) {
      return;
    }

    setError(null);
    setConnecting(true);

    try {
      const response = await apiService.post('/customers', {
        customerNumber: parseInt(customerNumber),
        zipCode
      });

      setCustomerData(response.data);
      await store.customers.load();
      setCustomerConnected(true);
    } catch (error) {
      console.error('Customer Connect Error:', error);
      setError(t('assistant.customerConnectStep.connectionError'));
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {!customerData ? (
        <>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'medium' }}>
            {t('assistant.customerConnectStep.title')}: {customerNumber}
          </Typography>

          <Typography variant="body2" sx={{ mb: 3 }}>
            {t('assistant.customerConnectStep.description', { customerNumber })}
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: '100%'
          }}>
            <TextField
              fullWidth
              label={t('assistant.customerConnectStep.zipCode')}
              value={zipCode}
              onChange={(e) => {
                setZipCode(e.target.value);
                setZipCodeError(null);
              }}
              onBlur={(e) => validateZipCode(e.target.value)}
              error={!!zipCodeError}
              helperText={zipCodeError}
              variant="outlined"
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  width: '100%'
                }
              }}
            />
          </Box>

          {error && (
            <Alert className="mt-3" variant="destructive">
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleConnect}
              disabled={!zipCode || connecting}
              sx={{ minWidth: 120 }}
            >
              {connecting ? (
                <CircularProgress size={24} />
              ) : (
                t('assistant.customerConnectStep.connect')
              )}
            </Button>
          </Box>
        </>
      ) : (
        <div className="space-y-4">
          <Alert>
            <Check className="h-4 w-4" />
            <AlertTitle>{t('assistant.customerConnectStep.customerConnected')}</AlertTitle>
          </Alert>

          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('assistant.customerConnectStep.customerInfo')}</h3>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('assistant.customerConnectStep.customerNumberLabel')}:</span>
                <span className="font-medium">{customerData.customerNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('assistant.customerConnectStep.businessNameLabel')}:</span>
                <span className="font-medium">{customerData.businessName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('assistant.customerConnectStep.ownerLabel')}:</span>
                <span className="font-medium">{customerData.owner}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('assistant.customerConnectStep.streetLabel')}:</span>
                <span className="font-medium">{customerData.streetOne}</span>
              </div>
              {customerData.streetTwo && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('assistant.customerConnectStep.streetAdditionLabel')}:</span>
                  <span className="font-medium">{customerData.streetTwo}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('assistant.customerConnectStep.zipCodeLabel')}:</span>
                <span className="font-medium">{customerData.zipCode}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Box>
  );
};

export default CustomerConnectStep;
